import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { StarRating } from '@/components/ui/StarRating';
import { Textarea } from '@/components/ui/textarea';
import {
  buildPostLiveFeedbackPayload,
  canSubmitPostLiveFeedback,
  emptyPostLiveFeedbackForm,
  getPostLiveFeedbackConfirmation,
  getPostLiveFeedbackStep,
  isCommentVisible,
  MAX_COMMENT_LENGTH,
  type PostLiveFeedbackConfirmation,
  type PostLiveFeedbackFormState,
  type PostLiveFeedbackSuggestionChoice,
} from '@/lib/postLiveFeedbackForm';
import { analyticsService } from '../api/analyticsService';

/** Le back bascule la session en FINISHED une dizaine de secondes après le clic sur « Clôturer »,
 *  alors que la redirection vers les analytics est immédiate : on relance une fois passé ce délai. */
const SESSION_CLOSE_SETTLE_DELAY_MS = 12_000;

/** L'avis est obligatoire en production seulement : ailleurs on doit pouvoir fermer pour tester. */
const IS_FEEDBACK_MANDATORY = import.meta.env.VITE_ENVIRONMENT === 'production';

interface PostLiveFeedbackDialogProps {
  workspaceId: number | undefined;
  sessionId: number | undefined;
  /** Statut de la session au chargement de la page, sert à décider de la relance différée. */
  isSessionFinished: boolean;
  /** Une session jamais lancée ne peut pas donner lieu à un avis : aucune requête. */
  isSessionStarted: boolean;
}

export const PostLiveFeedbackDialog = ({
  workspaceId,
  sessionId,
  isSessionFinished,
  isSessionStarted,
}: PostLiveFeedbackDialogProps) => {
  const { t } = useTranslation('analytics');
  const { t: tCommon } = useTranslation('common');

  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<PostLiveFeedbackFormState>(emptyPostLiveFeedbackForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<PostLiveFeedbackConfirmation | null>(null);

  useEffect(() => {
    if (!workspaceId || !sessionId || !isSessionStarted) return;

    let isStillMounted = true;
    let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let hasRetried = false;

    const checkEligibility = async () => {
      try {
        const eligibility = await analyticsService.getPostLiveFeedbackEligibility(
          workspaceId,
          sessionId,
        );
        if (!isStillMounted) return;
        if (eligibility.eligible) {
          setIsOpen(true);
        } else if (!eligibility.alreadySubmitted && !isSessionFinished && !hasRetried) {
          hasRetried = true;
          retryTimeoutId = setTimeout(() => void checkEligibility(), SESSION_CLOSE_SETTLE_DELAY_MS);
        }
      } catch {
        // Un avis manqué ne doit jamais dégrader la page Analytics.
      }
    };

    void checkEligibility();

    return () => {
      isStillMounted = false;
      clearTimeout(retryTimeoutId);
    };
  }, [workspaceId, sessionId, isSessionFinished, isSessionStarted]);

  const step = getPostLiveFeedbackStep(form.rating);
  const showsComment = isCommentVisible(form);
  const canSubmit = canSubmitPostLiveFeedback(form) && !isSubmitting;

  const handleRatingChange = (rating: number) =>
    setForm({ ...emptyPostLiveFeedbackForm, rating, comment: form.comment });

  const handleSuggestionChoice = (choice: PostLiveFeedbackSuggestionChoice) =>
    setForm((current) => ({
      ...current,
      suggestionChoice: current.suggestionChoice === choice ? null : choice,
    }));

  const handleSubmit = useCallback(async () => {
    const payload = buildPostLiveFeedbackPayload(form);
    if (!payload || !workspaceId || !sessionId) return;

    setIsSubmitting(true);
    try {
      await analyticsService.submitPostLiveFeedback(workspaceId, sessionId, payload);
      setConfirmation(getPostLiveFeedbackConfirmation(payload.rating));
    } catch {
      toast.error(tCommon('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  }, [form, workspaceId, sessionId, tCommon]);

  const handleOpenChange = (open: boolean) => setIsOpen(open);

  if (confirmation !== null) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-[624px] px-6 pb-12 pt-8 text-center">
          <DialogTitle className="pb-0 text-[28px] font-semibold text-dark">
            {t(`postLiveFeedback.confirmation.${confirmation}.title`)}
          </DialogTitle>
          <DialogDescription className="whitespace-pre-line text-lg text-secondary">
            {t(`postLiveFeedback.confirmation.${confirmation}.description`)}
          </DialogDescription>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        hideCloseButton
        className="flex max-w-[800px] flex-col gap-8 px-6 pb-12 pt-8"
        aria-describedby={undefined}
        onEscapeKeyDown={(event) => {
          if (IS_FEEDBACK_MANDATORY) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (IS_FEEDBACK_MANDATORY) event.preventDefault();
        }}
      >
        <div className="flex flex-col items-center gap-2 text-center">
          <DialogTitle className="pb-0 text-2xl font-semibold text-dark sm:text-[32px]">
            {t('postLiveFeedback.title')}
          </DialogTitle>
          <p className="text-lg font-medium text-secondary sm:text-xl">
            {t('postLiveFeedback.subtitlePrefix')}
            <span className="text-primary">Welya</span>
            {t('postLiveFeedback.subtitleSuffix')}
          </p>
        </div>

        <StarRating
          value={form.rating}
          onChange={handleRatingChange}
          disabled={isSubmitting}
          label={t('postLiveFeedback.ratingLabel')}
          className="justify-center"
        />

        {step === 'suggestion' && (
          <div className="flex flex-col gap-4">
            <p className="text-secondary">{t('postLiveFeedback.suggestion.question')}</p>
            <label className="flex w-fit cursor-pointer items-center gap-3">
              <Checkbox
                checked={form.suggestionChoice === 'YES'}
                onCheckedChange={() => handleSuggestionChoice('YES')}
                disabled={isSubmitting}
              />
              <span className="text-dark">{t('postLiveFeedback.suggestion.yes')}</span>
            </label>
            {showsComment && (
              <Textarea
                value={form.comment}
                maxLength={MAX_COMMENT_LENGTH}
                disabled={isSubmitting}
                onChange={(event) => setForm({ ...form, comment: event.target.value })}
                placeholder={t('postLiveFeedback.suggestion.placeholder')}
                className="min-h-[64px] rounded-xl text-base"
              />
            )}
            <label className="flex w-fit cursor-pointer items-center gap-3">
              <Checkbox
                checked={form.suggestionChoice === 'NOTHING_TO_REPORT'}
                onCheckedChange={() => handleSuggestionChoice('NOTHING_TO_REPORT')}
                disabled={isSubmitting}
              />
              <span className="text-dark">{t('postLiveFeedback.suggestion.nothingToReport')}</span>
            </label>
          </div>
        )}

        {step === 'improvement' && (
          <div className="flex flex-col gap-4">
            <p className="whitespace-pre-line text-secondary">
              {t('postLiveFeedback.improvement.question')}
            </p>
            <Textarea
              value={form.comment}
              maxLength={MAX_COMMENT_LENGTH}
              disabled={isSubmitting}
              onChange={(event) => setForm({ ...form, comment: event.target.value })}
              placeholder={t('postLiveFeedback.improvement.placeholder')}
              className="min-h-[104px] rounded-xl text-base"
            />
          </div>
        )}

        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
          className="mx-auto w-[280px] max-w-full"
        >
          {t('postLiveFeedback.submit')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
